import React, { useCallback, useEffect, useMemo, useReducer } from "react";

import GlobalContext from "./GlobalContext";

const apiUrl = '/wp-json/artsongs/v1';

const reducer = (state, action) => {
  switch(action.type)
  {
    case 'set error':
      return {
        ...state,
        error: true,
        errorMessage: action.errorMessage
      }
    case 'set filter':
      return {
        ...state,
        filters: { 
          ...state.filters,  
          [action.name]: action.selected
        }
      }
    case 'set filter categories':
      return {
        ...state,
        filterCategories: action.filterCategories,
        filterParentCategories: action.filterParentCategories
      }
    case 'set page':
      return {
        ...state,
        page: action.page
      }
    case 'set query':
      return {
        ...state,
        query: action.query
      }
    case 'set records':
      return {
        ...state,
        error: false,
        numPages: action.numPages,
        records: action.records
      }
    case 'set selected tile':
      return {
        ...state,
        selected: action.id
      }
    case 'set settings':
      return {
        ...state,
        categories: action.categories,
        excludeCategories: action.excludeCategories,
        initialized: true,
        limit: action.dataType === 'faq' ? 100 : 16,
        order: action.order,
        orderBy: action.orderBy,
        type: action.dataType
      }
    default:
  }
}

export default function GlobalState(props) {

  const [ state, dispatch ] = useReducer(reducer, {
    categories: [],
    error: false,
    errorMessage: null,
    excludeCategories: [], 
    filterCategories: {}, 
    filterParentCategories: [],
    filters: {}, 
    initialized: false,
    limit: 16,
    numPages: 1,
    order: 'asc',
    orderBy: 'title',
    page: 1,
    query: '',
    records: [],
    selected: null,
    selectedFilters: [],
    type: 'works'
  });

  const changeFilter = useCallback((options, event) => {
    const selected = options.map(option => option.value);
    dispatch({
      type: 'set filter',
      name: event.name,
      selected: selected
    });
  }, []);
  
  const changeFilterQuery = useCallback(query => {
    dispatch({
      type: 'set query',
      query: query
    });
  }, []);
  
  const changePage = useCallback((event, page) => {
    dispatch({
      type: 'set page',
      page: page
    });
  }, []);

  async function getFilters(){
    const root = document.getElementById('query-display-root');
    const filterParentCategories = JSON.parse(root.dataset.filtCat);

    const taxonomy = state.type === 'news' ? 'tag' : 'category';

    const params = new URLSearchParams({
      parentCat: filterParentCategories,
      taxonomy: taxonomy
    });
    const url = `${apiUrl}/filters?${params}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'cache-controle': 'no-cache'
      }
    });

    const data = await response.json();

    switch(true){
      case response.status >= 300:
        dispatch({
          type: 'set error',
          errorMessage: 'message' in data ? data.message : response.status
        });
        break;
      default:
        dispatch({
          type: 'set filter categories',
          filterCategories: data.filters,
          filterParentCategories: filterParentCategories
        });
    }
  }

  async function getResults(){

    const filters = new Set();
    const filtersRaw = Object.values(state.filters);
    
    if(filtersRaw.length > 0){
      filtersRaw.forEach(select => {
        select.forEach(category => {
          filters.add(category);
        })
      });
    }

    const params = new URLSearchParams({
      cat: state.categories,
      exCat: state.excludeCategories,
      filters: [ ...filters ],
      limit: state.limit,
      order: state.order,
      orderby: state.orderBy,
      page: state.page,
      query: state.query,
      type: state.type
    });
    const url = `${apiUrl}/query?${params}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'cache-control': 'no-cache'
      }
    });

    const data = await response.json();

    switch(true){
      case response.status >= 300:
        dispatch({
          type: 'set error',
          errorMessage: 'message' in data ? data.message : response.status
        });
        break;
      default:
        const main = document.querySelector('header+main');
        const root = document.getElementById('query-display-root');
        const top = main?.offsetTop;

        dispatch({
          type: 'set records',
          numPages: data.pages,
          page: 1,
          records: data.records
        });
        window.scrollTo(0, top);
        root.classList.remove('loading');
    }
  }

  function getSettings(){
    const root = document.getElementById('query-display-root');
    const data = root.dataset;
    const categories = JSON.parse(data.cat);
    const excludeCategories = JSON.parse(data.exCat);

    dispatch({
      type: 'set settings',
      categories: categories,
      dataType: data.type,
      excludeCategories: excludeCategories,
      order: data.order,
      orderBy: data.orderby
    });
  }

  const initialize = () => {
    getSettings();
    getFilters();
  }

  const selectTile = useCallback(id => {
    dispatch({
      type: 'set selected tile',
      id: id
    });
  }, []);

  const actions = useMemo(() => ({
    changeFilter: changeFilter,
    changeFilterQuery: changeFilterQuery,
    changePage: changePage,
    selectTile: selectTile
  }), [changeFilter, changeFilterQuery, changePage, selectTile]);

  const globalState = {
    actions: actions,
    refs: {},
    state: state,
  }

  useEffect(() => { 
    if(!state.initialized) initialize();
  }, [ state.initialized ]);

  useEffect(() => { 
    if(state.initialized) getResults();
  }, [ state.initialized, state.categories, state.excludeCategories, state.filters, state.page, state.query ]);

  return (
    <GlobalContext.Provider value={ globalState }>
      {props.children}
    </GlobalContext.Provider>
  );
}